import { useState } from "react";
import type { ClinicSession } from "../../shared/api/clinicSessions";
import type { WorkingHours } from "../../shared/api/clinic";
import { hoursOnWeekday } from "../../shared/workingHours";

const DAY_LABELS = ["T2", "T3", "T4", "T5", "T6", "T7", "CN"];

function toMin(hhmm: string): number {
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

const pct = (min: number) => `${(min / 1440) * 100}%`;

/** Khoảng trống giữa hai khoảng nhận lịch liền nhau (theo giờ bắt đầu). */
export function gapsBetween(sessions: readonly ClinicSession[]): { start: string; end: string }[] {
  const sorted = [...sessions].sort((a, b) => a.startTime.localeCompare(b.startTime));
  const out: { start: string; end: string }[] = [];
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i - 1].endTime < sorted[i].startTime) {
      out.push({ start: sorted[i - 1].endTime, end: sorted[i].startTime });
    }
  }
  return out;
}

/**
 * #19979 — một ngày 00–24h: khoảng nhận lịch là khối màu, Giờ làm việc của thứ đang xem là dải
 * nền. Nhìn một lần thấy khoảng trống và khoảng nào lố ra ngoài giờ làm việc — thứ trước đây phải
 * so từng dòng của hai trang khác nhau.
 *
 * Luật hiển thị khớp `WorkingHoursCeiling`: thứ chưa khai giờ = nhận lịch mọi giờ; khoảng thường
 * nằm ngoài giờ của thứ đó bị cắt phần ngoài; khoảng bật "Nhận lịch ngoài Giờ làm việc" thì không.
 */
export function SessionTimeline({
  sessions,
  workingHours,
}: {
  sessions: readonly ClinicSession[];
  workingHours: WorkingHours;
}) {
  const today = ((new Date().getDay() + 6) % 7) + 1; // 1 = Thứ 2 … 7 = CN
  const [weekday, setWeekday] = useState(today);
  const active = sessions.filter((s) => s.active);
  const hours = hoursOnWeekday(workingHours, weekday);
  const gaps = gapsBetween(active);
  const outside = active.filter(
    (s) => hours && !s.afterHours && (s.startTime < hours.open || s.endTime > hours.close),
  );

  if (active.length === 0) return null;

  return (
    <div className="mb-4 rounded-md border bg-card p-4" data-testid="session-timeline">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 className="m-0 text-sm font-semibold">Trong một ngày</h2>
        <div className="flex gap-1" role="group" aria-label="Chọn thứ">
          {DAY_LABELS.map((d, i) => (
            <button
              key={d}
              type="button"
              aria-pressed={weekday === i + 1}
              onClick={() => setWeekday(i + 1)}
              className={`h-7 min-w-8 rounded px-2 text-xs ${
                weekday === i + 1
                  ? "bg-primary text-primary-foreground"
                  : "bg-muted text-muted-foreground hover:bg-muted/70"
              }`}
            >
              {d}
            </button>
          ))}
        </div>
      </div>

      <div className="relative h-10 overflow-hidden rounded bg-muted/40">
        {hours && (
          <div
            data-testid="timeline-working-hours"
            className="absolute inset-y-0 bg-emerald-500/15"
            style={{ left: pct(toMin(hours.open)), width: pct(toMin(hours.close) - toMin(hours.open)) }}
            title={`Giờ làm việc ${hours.open}–${hours.close}`}
          />
        )}
        {active.map((s) => {
          const off = outside.includes(s);
          return (
            // Tên khoảng vẽ bằng CSS (attr(data-label)), không bằng text node: tên đã có ở bảng ngay
            // dưới, nhân đôi text node thì mọi tìm kiếm theo tên trên trang đều ra hai chỗ.
            <div
              key={s.id}
              role="img"
              data-testid="timeline-block"
              data-label={s.label}
              aria-label={`${s.label} ${s.startTime}–${s.endTime}`}
              title={`${s.label} ${s.startTime}–${s.endTime}`}
              className={`absolute inset-y-1 flex items-center justify-center overflow-hidden truncate rounded px-1 text-[11px] font-medium after:content-[attr(data-label)] ${
                off
                  ? "border border-dashed border-amber-500 bg-amber-200/70 text-amber-900"
                  : s.afterHours
                    ? "bg-sky-500/80 text-white"
                    : "bg-primary/85 text-primary-foreground"
              }`}
              style={{
                left: pct(toMin(s.startTime)),
                width: pct(toMin(s.endTime) - toMin(s.startTime)),
              }}
            />
          );
        })}
      </div>
      <div className="mt-1 flex justify-between font-mono text-[10px] text-muted-foreground">
        {["00", "06", "12", "18", "24"].map((h) => (
          <span key={h}>{h}:00</span>
        ))}
      </div>

      <ul className="m-0 mt-2 grid list-none gap-0.5 p-0 text-xs text-muted-foreground">
        <li>
          {hours
            ? `Giờ làm việc ${DAY_LABELS[weekday - 1]}: ${hours.open}–${hours.close} (dải nền xanh).`
            : `${DAY_LABELS[weekday - 1]} chưa khai Giờ làm việc — nhận lịch ở mọi giờ trong các khoảng dưới đây.`}
        </li>
        {gaps.length > 0 && (
          <li data-testid="timeline-gaps">
            Không nhận lịch giữa các khoảng:{" "}
            <span className="font-mono">{gaps.map((g) => `${g.start}–${g.end}`).join(" · ")}</span>
          </li>
        )}
        {active.some((s) => s.afterHours) && (
          <li>Khối xanh dương: khoảng bật "Nhận lịch ngoài Giờ làm việc".</li>
        )}
        {outside.length > 0 && (
          <li data-testid="timeline-outside" className="text-amber-700 dark:text-amber-300">
            {outside.map((s) => s.label).join(", ")} vượt Giờ làm việc {DAY_LABELS[weekday - 1]}:
            phần ngoài giờ không nhận lịch ngày này.
          </li>
        )}
      </ul>
    </div>
  );
}
